import React, { useEffect, useRef, useState } from 'react'
import { effectiveWindow, staleRangeLabel } from './tracpatch.window'
import { connect } from 'react-redux'
import { Row, Button } from 'antd'
import { CaretLeftOutlined, CaretRightOutlined } from '@ant-design/icons'
import * as d3 from 'd3'
import moment from 'moment'
import '../../../css/patient.scss'
import { stepRangeForDayOffset } from '../../schedule/tracpatch.step.defaults'
import CHART_COLORS from '../../../util/chartPalette'
import { DataViewToggle, ChartDataGrid } from '../../graph/chartDataView'
import { quarterHourSteps, quarterRangeLabel, compactHourLabel, earliestDay, isTodayOrLater } from './tracpatch.hourly'
import { styleAxisTicks, AXIS_TITLE_FONT_SIZE, AXIS_TITLE_FONT_WEIGHT, ShowValuesSwitch } from './tracpatch.chart.style'

const formatStepsLabel = (v) => {
  if (v > 999) {
    const k = v / 1000
    return (k % 1 === 0 ? k.toFixed(0) : k.toFixed(1)) + 'k'
  }
  return `${v}`
}

// Ranges: 1 Day (one local day, one point per 15 minutes, navigable a day at a
// time up to today) or 7 / 14 / 30 days (one point per day). Defaults to 30 Days.
const TracPatchStepsChart = (props) => {
  const svgRef = useRef(null)
  const containerRef = useRef(null)
  const tooltipRef = useRef(null)
  const [days, setDays] = useState(30)
  const [day, setDay] = useState(() => moment().startOf('day'))
  const [view, setView] = useState('chart')
  // Off by default: a number at every point turns a 30-day series into a wall
  // of digits. See tracpatch.chart.style.js.
  const [showValues, setShowValues] = useState(false)

  const hourly = days === 1

  useEffect(() => {
    if (view === 'chart') draw()
  }, [props.data, days, view, day.valueOf(), showValues]) // eslint-disable-line react-hooks/exhaustive-deps

  var getDailySteps = () => {
    if (!props.data || props.data.length === 0) return []

    // Falls back to the patient's latest reading when the requested range
    // holds none — see tracpatch.window.js.
    const win = effectiveWindow(props.data, days, (r) => r && r.startTime)
    const cutoff = win.windowStart

    // Group by day, sum steps. Also track the day's latest reading timestamp
    // so the data-grid can show the real date+time (the chart keeps the MM/DD
    // band label).
    const byDay = {}
    props.data.forEach((record) => {
      const t = moment(record.startTime)
      if (t.isBefore(cutoff)) return
      // Upper bound matters once the window can end before today.
      if (t.startOf('day').isAfter(win.windowEnd)) return
      const day = t.format('YYYY-MM-DD')
      if (!byDay[day]) byDay[day] = { steps: 0, lastTime: null }
      byDay[day].steps += record.steps
      if (!byDay[day].lastTime || t.isAfter(moment(byDay[day].lastTime))) {
        byDay[day].lastTime = record.startTime
      }
    })

    return Object.entries(byDay)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, v]) => ({ date: moment(date).format('MM/DD'), iso: date, time: v.lastTime, steps: v.steps }))
  }

  // 1 Day: the selected day in 96 fifteen-minute slots, each the total of the
  // records that started in it (the same resolution as Cadence and
  // Temperature). Every slot is a band so the axis reads as a clock; a slot
  // with no record is null, so the line breaks there instead of reading zero.
  // An empty day yields [] so the chart shows its empty state, not 96 zeros.
  var getQuarterHourSteps = () => {
    const slots = quarterHourSteps(props.data, day)
    if (!slots.some((s) => s.steps > 0)) return []
    return slots.map((s) => ({
      date: moment(s.date).format('h:mm a'), // band key, unique per slot
      slot: s.slot,
      hour: s.hour,
      iso: moment(s.date).format('YYYY-MM-DD'),
      at: s.date,
      time: s.time,
      steps: s.steps,
    }))
  }

  var getSeries = () => (hourly ? getQuarterHourSteps() : getDailySteps())

  var draw = () => {
    const dailyData = getSeries()
    if (dailyData.length === 0) return

    const margin = { top: 30, right: 20, bottom: 60, left: 55 }
    const screenWidth = containerRef.current ? containerRef.current.offsetWidth : 400
    const screenHeight = 300
    const width = screenWidth - margin.left - margin.right
    const height = screenHeight - margin.top - margin.bottom

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()
    // A redraw replaces the point under the pointer, so its mouseout never
    // fires and a tooltip left visible would float there until the next
    // hover. Every draw therefore starts with the tooltip hidden.
    d3.select(tooltipRef.current).style('visibility', 'hidden')

    const g = svg
      .attr('width', screenWidth)
      .attr('height', screenHeight)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`)

    // POINT scale, not band. A band scale reserves a slot per day and centres
    // the marker inside it, so the first point sat half a band in from the axis
    // while Cadence -- a time scale -- started hard against it. Now that Steps
    // is a line rather than bars there is no slot to reserve: scalePoint puts
    // the first point at 0 and the last at `width`, matching every other chart.
    //
    // `.bandwidth()` still exists on a point scale and returns 0, so the
    // `+ x.bandwidth() / 2` centring below stays correct and untouched.
    const x = d3.scalePoint()
      .domain(dailyData.map((d) => d.date))
      .range([0, width])
      .padding(0)

    // Expected step-range band (min/max) per day, looked up by the day's offset
    // from the surgery date. Each chart day maps to a clamped day-since-surgery
    // and its { min, max } range; drawn later as two black lines over the bars.
    // Per-day ranges have no hourly meaning, so the 1-Day view skips them.
    const surgeryMoment = props.patient?.surgery?.date ? moment(props.patient.surgery.date) : null
    const rangeData = (!hourly && surgeryMoment && surgeryMoment.isValid())
      ? dailyData
          .map((d) => {
            const offset = moment(d.iso).startOf('day').diff(surgeryMoment.clone().startOf('day'), 'days')
            const r = stepRangeForDayOffset(offset)
            return r ? { date: d.date, min: r.min, max: r.max } : null
          })
          .filter(Boolean)
      : []

    // Scale the y axis to fit both the bars and the expected-range lines.
    const yMax = Math.max(
      1,
      ...dailyData.filter((d) => d.steps != null).map((d) => d.steps),
      ...rangeData.map((d) => d.max),
    )
    const y = d3.scaleLinear().domain([0, yMax * 1.15]).range([height, 0])

    // X axis. 96 fifteen-minute bands cannot all carry a label, so the 1-Day
    // axis ticks every 2 hours ("12a, 2a, ... 10p"), as Cadence and
    // Temperature do; the bands in between still position the points.
    const xAxis = d3.axisBottom(x)
    if (hourly) {
      const hourOf = {}
      dailyData.forEach((d) => { hourOf[d.date] = d.hour })
      xAxis
        .tickValues(dailyData.filter((d) => d.slot % 8 === 0).map((d) => d.date))
        .tickFormat((key) => compactHourLabel(hourOf[key]))
    }
    g.append('g')
      .attr('transform', `translate(0,${height})`)
      .call(xAxis)
      .selectAll('text')
      .style('text-anchor', 'end')
      .attr('dx', '-.8em')
      .attr('dy', '.15em')
      .attr('transform', 'rotate(-45)')
      .style('fill', 'black')
      .style('stroke', 'none')
      .call(styleAxisTicks, !hourly && dailyData.length > 20 ? 9 : 11)

    // X label
    g.append('text')
      .attr('text-anchor', 'end')
      .attr('x', width / 2 + margin.left)
      .attr('y', height + margin.bottom - 5)
      .attr('stroke', 'none')
      .attr('fill', 'black')
      .style('font-size', AXIS_TITLE_FONT_SIZE)
      .style('font-weight', AXIS_TITLE_FONT_WEIGHT)
      .text(hourly ? 'Time' : 'Date')

    // Y axis
    g.append('g')
      .call(d3.axisLeft(y).ticks(6).tickFormat(d3.format(',.0f')))
      .selectAll('text')
      .style('fill', 'black')
      .style('stroke', 'none')
      .call(styleAxisTicks, 10)

    // Y label
    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', -margin.left + 12)
      .attr('x', -height / 2)
      .attr('text-anchor', 'middle')
      .attr('fill', 'black')
      .attr('stroke', 'none')
      .style('font-size', AXIS_TITLE_FONT_SIZE)
      .style('font-weight', AXIS_TITLE_FONT_WEIGHT)
      .text('Steps')

    // Surgery-date marker — vertical dashed black line + "Surgery M/D"
    // label. Drawn before the bars so the bars sit on top. Daily views only:
    // the hourly axis has no date bands for it to land on.
    const surgeryRaw = !hourly && props.patient?.surgery?.date
    if (surgeryRaw) {
      const sd = moment(surgeryRaw)
      if (sd.isValid()) {
        const surgeryKey = sd.format('MM/DD')
        if (x(surgeryKey) != null) {
          const sx = x(surgeryKey) + x.bandwidth() / 2
          g.append('line')
            .attr('x1', sx)
            .attr('x2', sx)
            .attr('y1', 0)
            .attr('y2', height)
            .attr('stroke', '#000')
            .attr('stroke-width', 1.5)
            .attr('stroke-dasharray', '4,3')
          const labelText = 'Surgery ' + sd.format('M/D')
          const labelOnLeft = sx > width - 80
          g.append('text')
            .attr('x', sx + (labelOnLeft ? -4 : 4))
            .attr('y', -8)
            .attr('text-anchor', labelOnLeft ? 'end' : 'start')
            .style('font-size', '11px')
            .style('font-weight', '600')
            .style('fill', '#000')
            .text(labelText)
        }
      }
    }

    const tooltip = d3.select(tooltipRef.current)
    const whenLabel = (d) => (hourly ? `<b>Time</b>: ${quarterRangeLabel(d.slot)}` : `<b>Date</b>: ${d.date}`)

    // Steps as a LINE (was one bar per band). A line reads as a trend, which is
    // what a 30-day steps series is for; bars invited comparison between
    // adjacent days that the data does not really support. The band scale is
    // kept so the axis labels and the surgery marker still land on bands --
    // the line simply uses each band's centre.
    const cx = (d) => x(d.date) + x.bandwidth() / 2

    // .defined() BREAKS the line at hours with no readings instead of drawing a
    // straight run through them. A connected line across a gap asserts data
    // that does not exist -- and across the rest of today, it asserted zero.
    g.append('path')
      .datum(dailyData)
      .attr('fill', 'none')
      .attr('stroke', CHART_COLORS.lineBlue)
      .attr('stroke-width', 2)
      .attr('stroke-linejoin', 'round')
      .attr('d', d3.line().defined((d) => d.steps != null).x(cx).y((d) => y(d.steps)))

    // A point per reading, and the click target for drilling into a day.
    g.selectAll('.step-dot')
      .data(dailyData.filter((d) => d.steps != null))
      .enter()
      .append('circle')
      .attr('class', 'step-dot')
      .attr('cx', cx)
      .attr('cy', (d) => y(d.steps))
      .attr('r', hourly ? 2 : dailyData.length > 20 ? 2.5 : 4)
      .attr('fill', CHART_COLORS.lineBlue)
      .style('cursor', hourly ? 'pointer' : 'zoom-in')
      .on('mouseover', function (d) {
        tooltip
          .style('visibility', 'visible')
          .html(
            `${whenLabel(d)}<br/><b>Steps</b>: ${d.steps.toLocaleString()}` +
              (hourly ? '' : '<br/><i>Click for this day</i>')
          )
          .style('top', y(d.steps) - 46 + 'px')
          .style('left', cx(d) + 10 + 'px')
      })
      .on('mouseout', () => tooltip.style('visibility', 'hidden'))
      // Click a day -> that day's hourly view. `iso` is the local YYYY-MM-DD
      // the day was bucketed under, so the drill-down lands on the same day
      // the label showed rather than a UTC-shifted neighbour.
      .on('click', (d) => {
        if (hourly || !d.iso) return
        tooltip.style('visibility', 'hidden')
        setDay(moment(d.iso, 'YYYY-MM-DD').startOf('day'))
        setDays(1)
      })

    // Value labels -- OFF unless the chart's "Show values" switch is on.
    if (showValues) {
      const labelFontSize = hourly || dailyData.length > 20 ? '9px' : '10px'
      g.selectAll('.point-label')
        .data(dailyData.filter((d) => d.steps != null && d.steps > 0))
        .enter()
        .append('text')
        .attr('class', 'point-label')
        .attr('x', cx)
        .attr('y', (d) => y(d.steps) - 8)
        .attr('text-anchor', 'middle')
        .style('font-size', labelFontSize)
        .style('fill', '#333')
        .style('font-weight', '600')
        .style('pointer-events', 'none')
        .text((d) => formatStepsLabel(d.steps))
    }

    // Expected min/max range as two dashed green lines over the bars.
    if (rangeData.length > 0) {
      ;['min', 'max'].forEach((key) => {
        const lineGen = d3.line().x(cx).y((d) => y(d[key]))
        g.append('path')
          .datum(rangeData)
          .attr('fill', 'none')
          .attr('stroke', CHART_COLORS.completeGreen)
          .attr('stroke-width', 2)
          .attr('stroke-dasharray', '6,4')
          .attr('stroke-linejoin', 'round')
          .attr('d', lineGen)
        g.selectAll('.range-dot-' + key)
          .data(rangeData)
          .enter()
          .append('circle')
          .attr('cx', cx)
          .attr('cy', (d) => y(d[key]))
          .attr('r', 2.5)
          .attr('fill', CHART_COLORS.completeGreen)
        // The guided lines carry values too -- same switch, same rule.
        if (showValues) {
          g.selectAll('.range-label-' + key)
            .data(rangeData)
            .enter()
            .append('text')
            .attr('class', 'range-label-' + key)
            .attr('x', cx)
            .attr('y', (d) => y(d[key]) + (key === 'max' ? -6 : 12))
            .attr('text-anchor', 'middle')
            .style('font-size', '9px')
            .style('fill', CHART_COLORS.completeGreen)
            .style('font-weight', '600')
            .style('pointer-events', 'none')
            .text((d) => formatStepsLabel(d[key]))
        }
      })

      // Legend: green dashed swatch + label, top-left of the plot area (left-aligned
      // so the full "(min/max)" label can't run off the right edge).
      const legend = g.append('g').attr('transform', 'translate(0, -18)')
      legend.append('line')
        .attr('x1', 0).attr('x2', 18).attr('y1', 4).attr('y2', 4)
        .attr('stroke', CHART_COLORS.completeGreen).attr('stroke-width', 3)
        .attr('fill', '6,4')
      legend.append('text')
        .attr('x', 24).attr('y', 8)
        .style('font-size', '11px').style('fill', '#333').style('stroke', 'none')
        .text('Expected range (min/max)')
    }
  }

  const dailyData = getSeries()
  const stale = effectiveWindow(props.data, days, (r) => r && r.startTime)
  // 1 Day rows are keyed by the slot's start ("10:15 AM"), so the grid and the
  // CSV read as the same 15-minute clock the chart shows.
  const gridRows = dailyData
    .filter((d) => d.steps != null && (!hourly || d.steps > 0))
    .map((d) => ({ date: hourly ? d.at : d.time || d.iso, value: d.steps.toLocaleString() }))

  // Day navigation (1 Day only): back one day at a time as far as the first
  // record, forward one day at a time up to today.
  const firstDay = earliestDay(props.data, 'startTime')
  const canGoBack = !!firstDay && day.isAfter(firstDay)
  const canGoForward = !isTodayOrLater(day)
  const shiftDay = (n) => setDay(day.clone().add(n, 'day').startOf('day'))

  return (
    <>
      <center>
        <h4>TracPatch Steps</h4>
      </center>
      {!hourly && stale.isStale && (
        <Row justify='start' style={{ marginBottom: 8, paddingLeft: 12 }}>
          <div style={{ background:'#fff8e1', border:'1px solid var(--orange-color, #f5881f)',
                        borderRadius:8, padding:'6px 12px', fontSize:12, color:'#8a4b00', maxWidth:'100%' }}>
            {staleRangeLabel(stale.windowStart, stale.windowEnd, days)}
          </div>
        </Row>
      )}
      <Row justify='start' className='chartControls' style={{ marginBottom: 8, paddingLeft: 12 }}>
        <Button.Group size='small'>
          <Button type={days === 1 ? 'primary' : 'default'} onClick={() => setDays(1)}>1 Day</Button>
          <Button type={days === 7 ? 'primary' : 'default'} onClick={() => setDays(7)}>7 Days</Button>
          <Button type={days === 14 ? 'primary' : 'default'} onClick={() => setDays(14)}>14 Days</Button>
          <Button type={days === 30 ? 'primary' : 'default'} onClick={() => setDays(30)}>30 Days</Button>
        </Button.Group>
        <ShowValuesSwitch id='steps-show-values' checked={showValues} onChange={setShowValues} />
        <DataViewToggle
          active={view === 'data'}
          onClick={() => setView(view === 'data' ? 'chart' : 'data')}
        />
      </Row>
      {hourly && (
        <Row justify='center' align='middle' className='chartDayNav' style={{ marginBottom: 8, gap: 8 }}>
          <Button
            size='small'
            aria-label='Previous day'
            icon={<CaretLeftOutlined />}
            disabled={!canGoBack}
            onClick={() => shiftDay(-1)}
          />
          <span style={{ minWidth: 150, textAlign: 'center', fontWeight: 600 }}>{day.format('ddd, MMM D, YYYY')}</span>
          <Button
            size='small'
            aria-label='Next day'
            icon={<CaretRightOutlined />}
            disabled={!canGoForward}
            onClick={() => shiftDay(1)}
          />
        </Row>
      )}
      {dailyData.length > 0 ? (
        view === 'data' ? (
          <ChartDataGrid rows={gridRows} valueLabel='Steps' csvName='tracpatch-steps' />
        ) : (
          <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
            <div ref={tooltipRef} className='chartToolTip' style={{ position: 'absolute', visibility: 'hidden', zIndex: 10 }} />
            <svg style={{ width: '100%', height: '300px' }} ref={svgRef} />
          </div>
        )
      ) : (
        <Row justify='center' align='middle' style={{ height: 200 }}>
          <h2>{hourly ? 'No steps recorded for this day' : 'Data not available'}</h2>
        </Row>
      )}
    </>
  )
}

const mapStateToProps = (state) => ({ patient: state.patient.patient })
const mapDispatchToProps = () => ({})
export default connect(mapStateToProps, mapDispatchToProps)(TracPatchStepsChart)
